<template>
  <div v-if="!isComponent" class="fixed-header">
    <appbar-view />
  </div>

  <div v-if="isLoading" class="d-flex flex-column justify-center align-center" :style="isComponent ? 'padding: 60px 0;' : 'height: 60vh;'">
    <v-progress-circular indeterminate color="blue-darken-4" size="60"></v-progress-circular>
    <span class="mt-4 text-body-1 font-weight-bold" :class="isComponent ? 'text-white' : 'text-blue-grey-darken-3'">Cargando pedidos...</span>
  </div>

<div v-else class="w-100" :class="{ 'scroll-container': !isComponent }">    <v-container :class="isComponent ? 'px-0 pt-0 pb-4' : 'px-4 pt-2 pb-4'" fluid style="max-width: 900px; margin: 0 auto;">
      
      <v-sheet class="mb-4 pa-4 rounded-xl elevation-1 bg-white">
        <div class="d-flex align-center justify-space-between">
          <div class="d-flex align-center">
            <v-avatar color="blue-lighten-5" size="36" class="mr-3" v-if="!isComponent">
              <v-icon color="blue-darken-3" size="20">mdi-script-text-outline</v-icon>
            </v-avatar>
            <div>
              <h1 class="text-subtitle-1 font-weight-bold text-grey-darken-3 line-height-1" :class="isComponent ? 'text-blue-darken-4 text-h6' : ''">Mis Pedidos</h1>
              <p class="text-caption text-grey mb-0">{{ items.length }} documentos</p>
            </div>
          </div>
        </div>
      </v-sheet>

      <div v-if="items.length === 0" class="text-center pa-6">
        <v-icon size="48" color="grey-lighten-1" class="mb-3">mdi-package-variant-closed</v-icon>
        <p :class="isComponent ? 'text-white' : 'text-grey'">No hay pedidos con estos filtros.</p>
      </div>

      <v-row dense v-else>
        <v-col v-for="item in items" :key="item.unique_id" cols="12" sm="6">
          <v-card class="factura-card rounded-xl cursor-pointer mb-2" elevation="2" @click="showProducto(item)" v-ripple>
            <div :class="`status-strip bg-${getStatusColor(item.estado)}`"></div>
            <v-card-text class="pa-4 bg-white">
              <div class="d-flex justify-space-between align-start mb-3">
                <div>
                  <div class="text-caption text-grey font-weight-bold text-uppercase mb-0">{{ item.factura ? 'FACTURA' : 'PEDIDO' }}</div>
                  <div class="text-subtitle-1 font-weight-black text-blue-grey-darken-3 line-height-1">{{ item.referencia_frog || item.pedido_id }}</div>
                </div>
                <v-chip :color="getStatusColor(item.estado)" size="x-small" variant="tonal" class="font-weight-bold text-uppercase" label>
                  <v-icon start size="small">{{ getStatusIcon(item.estado) }}</v-icon>{{ getStatusText(item.estado) }}
                </v-chip>
              </div>
              <v-divider class="mb-3 border-dashed"></v-divider>
              <div class="d-flex justify-space-between align-end">
                <div>
                  <div class="d-flex align-center text-caption text-grey-darken-1 mb-1"><v-icon size="14" class="mr-1">mdi-calendar-clock</v-icon>{{ formatDate(item.fecha_pedido) }}</div>
                  <div class="d-flex align-center text-caption text-grey-darken-1"><v-icon size="14" class="mr-1">mdi-map-marker-path</v-icon>Ruta: {{ item.ruta_preventa || "N/A" }}</div>
                </div>
                <div class="text-right">
                  <div class="text-caption text-grey mb-0" style="font-size: 0.65rem;">Total Neto</div>
                  <div class="text-h6 font-weight-black text-green-darken-1 line-height-1">${{ formatMoney(item.total) }}</div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </div>

  <v-dialog v-model="dialogDetalle" max-width="500" scrollable transition="dialog-bottom-transition">
    <v-card class="rounded-xl overflow-hidden bg-grey-lighten-4">
      
      <v-sheet class="pt-6 pb-4 px-6 text-center position-relative" color="white" elevation="1" style="z-index: 2;">
        <v-btn icon="mdi-close" variant="text" size="small" class="position-absolute top-0 right-0 ma-2" @click="dialogDetalle = false"></v-btn>
        
        <v-img :src="logoBase64" width="140" class="mx-auto mb-4 mt-2"></v-img>
        
        <h3 class="text-subtitle-1 font-weight-black text-blue-grey-darken-4 text-uppercase">
          {{ detallePedido?.factura ? 'FACTURA ELECTRÓNICA' : 'NOTA DE VENTA' }}
        </h3>
        <div class="text-body-2 text-grey-darken-2 font-weight-medium">Folio: <span class="font-weight-bold text-black">{{ detallePedido?.factura || detallePedido?.referencia_frog }}</span></div>
        
        <v-chip size="x-small" variant="flat" :color="getStatusColor(detallePedido?.estado)" class="mt-3 text-uppercase font-weight-bold">
          {{ getStatusText(detallePedido?.estado) }}
        </v-chip>
      </v-sheet>

      <v-card-text class="pa-0">
        <v-sheet class="pa-4 mx-3 my-3 rounded-lg elevation-0 bg-white" border>
          
          <v-row dense class="mb-2 text-caption">
            <v-col cols="12" class="mb-2 pb-2" style="border-bottom: 1px solid #eee;">
              <div class="text-grey mb-1">Cliente</div>
              <div class="font-weight-bold text-body-2 text-blue-grey-darken-4 line-height-1">
                {{ user.cliente }} - {{ user.nombre || 'Mostrador' }}
              </div>
            </v-col>
            <v-col cols="6">
              <div class="text-grey">Emisión</div>
              <div class="font-weight-bold text-black">{{ formatDate(detallePedido?.fecha_pedido) }}</div>
            </v-col>
            <v-col cols="6" class="text-right">
              <div class="text-grey">UUID (SAT)</div>
              <div class="font-weight-bold text-black text-truncate" style="max-width: 120px; float: right;">
                {{ datosFiscales.uuid || 'N/A' }}
              </div>
            </v-col>
          </v-row>

          <v-divider class="my-3 border-dashed"></v-divider>

          <div class="text-caption font-weight-bold text-blue-grey-darken-2 mb-3">PRODUCTOS ({{ detallePedido?.detalles?.length }})</div>
          
          <div v-for="(detalle, i) in detallePedido?.detalles" :key="i" class="mb-3 pb-3" style="border-bottom: 1px dashed #f0f0f0;">
            <div class="d-flex justify-space-between align-start">
              <div class="d-flex" style="width: 70%;">
                <v-avatar color="grey-lighten-4" size="32" class="mr-3 font-weight-bold text-caption text-blue-grey-darken-3 rounded">
                  {{ detalle.cantidad }}
                </v-avatar>
                <div>
                  <div class="font-weight-bold text-body-2 text-grey-darken-4 line-height-1 mb-1">{{ detalle.nombre }}</div>
                  <div class="text-caption text-grey">P.U: ${{ formatMoney(detalle.PEDCTED_COSTO) }} &bull; {{ detalle.unidad }}</div>
                </div>
              </div>
              <div class="text-right" style="width: 30%;">
                <div class="font-weight-bold text-body-2 text-black">${{ formatMoney(detalle.subtotal) }}</div>
              </div>
            </div>
          </div>

          <v-sheet color="blue-grey-lighten-5" class="pa-3 rounded-lg mt-4">
            <div class="d-flex justify-space-between text-caption mb-1">
              <span class="text-grey-darken-2">Subtotal</span>
              <span class="font-weight-bold">${{ formatMoney(detallePedido?.total - (detallePedido?.impuestos || 0)) }}</span>
            </div>
            <div class="d-flex justify-space-between text-caption mb-2">
              <span class="text-grey-darken-2">Impuestos (IVA)</span>
              <span class="font-weight-bold">${{ formatMoney(detallePedido?.impuestos || 0) }}</span>
            </div>
            <v-divider class="mb-2 border-black"></v-divider>
            <div class="d-flex justify-space-between align-center">
              <span class="text-subtitle-2 font-weight-black text-blue-grey-darken-4">TOTAL</span>
              <span class="text-h6 font-weight-black text-green-darken-2">${{ formatMoney(detallePedido?.total) }}</span>
            </div>
          </v-sheet>

        </v-sheet>

        <v-sheet v-if="detallePedido?.factura" class="pa-4 bg-transparent d-flex flex-column gap-2 mb-4">
            <div class="text-caption text-center text-grey mb-1">
              Obtén la representación impresa legal con el desglose del SAT:
            </div>
            <v-btn block color="blue-grey-darken-3" prepend-icon="mdi-file-pdf-box" variant="flat" rounded="lg" class="text-none mb-2" @click="downloadPDF">
              Descargar Documento Oficial PDF
            </v-btn>
            
            <div v-if="detallePedido?.xml" class="d-flex gap-2">
              <v-btn style="flex: 1;" color="white" prepend-icon="mdi-eye" variant="flat" rounded="lg" border class="text-none text-blue-grey" @click="verXML">Ver XML</v-btn>
              <v-btn style="flex: 1;" color="white" prepend-icon="mdi-code-json" variant="flat" rounded="lg" border class="text-none text-blue-grey" @click="downloadXML">Descargar XML</v-btn>
            </div>
        </v-sheet>
        <v-sheet v-else class="pa-4 bg-transparent d-flex flex-column mb-4 align-center text-center">
            <v-icon color="grey-lighten-1" size="32" class="mb-2">mdi-receipt-text-outline</v-icon>
            <div class="text-caption text-grey">Este documento es una Nota de Venta interna.<br>No cuenta con representación CFDI (PDF/XML) para descarga.</div>
        </v-sheet>
      </v-card-text>
    </v-card>
  </v-dialog>

  <bottom-navigation v-if="!isComponent" />
</template>

<script>
import axios from "axios";
import { useUserStore } from "../stores/userStore";

import jsPDF from "jspdf";
import "jspdf-autotable";
import QRCode from "qrcode";

import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory, Encoding } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';

export default {
  props: {
    isComponent: { type: Boolean, default: false }
  },
  components: {  },
  data() {
    return {
      logoBase64: "data:image/png;base64,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",
      localPedidos: [],
      localLoading: true,
      localLoadingFetchTriggered: false,
      dialogDetalle: false,
      detallePedido: null
    };
  },
  computed: {
    userStore() {
      return useUserStore();
    },
    user() {
      return this.userStore.user || {};
    },
    items() {
      return this.localPedidos;
    },
    isLoading() {
      return this.localLoading;
    },
    datosFiscales() {
      return this.getFiscalData(this.detallePedido?.xml) || {};
    }
  },
  watch: {
    userStore: {
      handler(store) {
        if (store.isSessionReady) {
          if (store.user && store.user.cliente) {
            if (this.localPedidos.length === 0 && !this.localLoadingFetchTriggered) {
               this.localLoadingFetchTriggered = true;
               this.obtenerConsultaPedidos();
            }
          } else {
            this.localLoading = false;
          }
        }
      },
      deep: true,
      immediate: true
    }
  },
  methods: {
    getStatusColor(status) { const s = Number(status); if (s===20) return 'blue'; if (s===50) return 'orange'; if (s===70) return 'purple'; if (s===90) return 'green'; if (s===40) return 'red'; return 'grey'; },
    getStatusIcon(status) { const s = Number(status); if (s===20) return 'mdi-file-document-check'; if (s===50) return 'mdi-truck-fast'; if (s===70) return 'mdi-file-certificate'; if (s===90) return 'mdi-check-decagram'; if (s===40) return 'mdi-close-octagon'; return 'mdi-help-circle'; },
    getStatusText(status) { const s = Number(status); if (s===20) return 'APROBADO'; if (s===50) return 'LIQUIDANDO'; if (s===70) return 'FACTURADO'; if (s===90) return 'ENTREGADO'; if (s===40) return 'CANCELADO'; return 'PENDIENTE'; },
    formatDate(dateString) { if (!dateString) return ''; return new Date(dateString).toLocaleDateString("es-MX", { day: '2-digit', month: '2-digit', year: 'numeric' }); },
    formatMoney(amount) { return Number(amount || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 }); },

    async obtenerConsultaPedidos() {
      this.localLoading = true;
      try {
        if (!this.user || !this.user.cliente) {
           this.localLoading = false;
           return;
        }
        const token = "PU8+iQv+EnvHLqmCoqFOhsJ72R4CYKXlmyt85Fat/zepmBy2992yioKQsu9SRoYByhAaFinAYSHko91lJbJ3ySkimqCZ0UgiLKbEHrHQ+SsOmG5OzaCawqBOVAZGJX5Ck9OJfKXxOZY27YYNeAMYMA==";

        const url = "https://api.ecore.ninja:1950/SP/EN_GPOMVPRO/UltimosPedidos";

        const responses = await Promise.all([
          axios.post(url, { CTECLI_CODIGO_K: this.user.cliente, CTEDIR_CODIGO_K: 1 }, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
          axios.post(url, { CTECLI_CODIGO_K: `MM${this.user.cliente}`, CTEDIR_CODIGO_K: 1 }, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] }))
        ]);

        let combinedData = responses.flatMap(res => Array.isArray(res.data) ? res.data : (res.data ? [res.data] : []));
        const uniqueMap = new Map();
        combinedData.forEach(item => { 
          if(item.PEDCTE_FOLIO_K) {
             const key = `${item.PEDCTE_SERIE_K || 'S'}-${item.PEDCTE_FOLIO_K}`;
             uniqueMap.set(key, item); 
          }
        });
        const rawData = Array.from(uniqueMap.values());

        this.localPedidos = rawData.map(order => {
          let detalles = [];
          let total = 0; let impuestos = 0; let descuentos = 0;

          if (order.Detalle && order.Detalle.length > 0) {
             total = order.Detalle.reduce((acc, item) => acc + (item.PEDCTED_TOTAL || 0), 0);
             impuestos = order.Detalle.reduce((acc, item) => acc + (item.PEDCTED_IMPUESTOS || 0), 0);
             descuentos = order.Detalle.reduce((acc, item) => acc + (item.PEDCTED_DESCUENTOS || 0), 0);
             const nombresProductos = this.getNamesMapFromXML(order.FACFIS_XML);
             detalles = order.Detalle.map(d => {
                let cantidad = d.PEDCTED_CANTLIQ || 0;
                let unidad = d.PROUND_CODIGO_K;
                if (cantidad === 0 && (d.PEDCTED_CANTLIQPZA || 0) > 0) {
                    if (d.PEDCTED_COSTO > 0) {
                        cantidad = parseFloat((d.PEDCTED_TOTAL / d.PEDCTED_COSTO).toFixed(4));
                    } else {
                        cantidad = 0.5;
                    }
                }
                return {
                  ...d,
                  nombre: nombresProductos[d.PRODUC_CODIGO_K]?.desc || d.PRODUC_CODIGO_K,
                  claveSat: nombresProductos[d.PRODUC_CODIGO_K]?.claveSat || "01010101",
                  claveUnidadSat: nombresProductos[d.PRODUC_CODIGO_K]?.claveUnidadSat || "H87",
                  objetoImp: nombresProductos[d.PRODUC_CODIGO_K]?.objetoImp || "02",
                  codigo: d.PRODUC_CODIGO_K,
                  unidad: unidad,
                  cantidad: cantidad,
                  subtotal: d.PEDCTED_TOTAL,
                  descuento: d.PEDCTED_DESCUENTOS || 0,
                  impuesto: d.PEDCTED_IMPUESTOS || 0,
                  PEDCTED_COSTO: d.PEDCTED_COSTO
                };
             });
          } 
          else if (order.FACFIS_XML) {
             detalles = this.getDetailsFromXML(order.FACFIS_XML);
             if (detalles.length > 0) {
                total = detalles.reduce((acc, item) => acc + item.subtotal, 0);
                impuestos = detalles.reduce((acc, item) => acc + item.impuesto, 0);
                descuentos = detalles.reduce((acc, item) => acc + item.descuento, 0);
             }
          }

          return {
            unique_id: `${order.PEDCTE_SERIE_K || 'S'}-${order.PEDCTE_FOLIO_K}`,
            pedido_id: order.PEDCTE_FOLIO_K,
            referencia_frog: order.PEDCTE_REFERENCIA,
            factura: order.FACDOC_REFERENCIAINI,
            fecha_pedido: order.PEDCTE_FECHAVTA,
            fecha_entrega: order.PEDCTE_FECHAENT,
            ruta_preventa: order.VTARUT_CODIGO_K,
            almacen: order.Detalle?.[0]?.CFGALM_CODIGO_K || 'GENERAL',
            estado: order.S_MAQEDO, 
            total: total, impuestos: impuestos, descuentos: descuentos,
            udn: order.SYSUDN_CODIGO_K,
            forma_pago: order.CTECLI_FORMAPAGO,
            metodo_pago: order.CTECLI_METODOPAGO,
            uso_cfdi: order.SAT_USO_CFDI_K,
            xml: order.FACFIS_XML,
            detalles: detalles
          };
        }).sort((a, b) => {
          const dateA = new Date(a.fecha_pedido);
          const dateB = new Date(b.fecha_pedido);
          const diff = dateB - dateA;

          if (diff !== 0) return diff;
          return Number(b.pedido_id) - Number(a.pedido_id);
        });

      } catch (error) { console.error("Error pedidos:", error); } finally { this.localLoading = false; }
    },
    
    async showProducto(pedido) { this.detallePedido = pedido; this.dialogDetalle = true; },
    
    getFiscalData(xmlString) {
      if (!xmlString) return null;
      try {
        const decoded = this.decodeXML(xmlString);
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(decoded, "text/xml");
        const tfd = xmlDoc.getElementsByTagName("tfd:TimbreFiscalDigital")[0] || xmlDoc.getElementsByTagName("TimbreFiscalDigital")[0];
        const comprobante = xmlDoc.getElementsByTagName("cfdi:Comprobante")[0] || xmlDoc.getElementsByTagName("Comprobante")[0];
        const emisor = xmlDoc.getElementsByTagName("cfdi:Emisor")[0] || xmlDoc.getElementsByTagName("Emisor")[0];
        const receptor = xmlDoc.getElementsByTagName("cfdi:Receptor")[0] || xmlDoc.getElementsByTagName("Receptor")[0];

        if (!tfd) return null;
        let fechaEmi = comprobante ? comprobante.getAttribute("Fecha") : "";
        let fechaTimb = tfd.getAttribute("FechaTimbrado") || "";

        return {
          uuid: tfd.getAttribute("UUID") || "",
          fechaTimbrado: fechaTimb.replace("T", " "),
          rfcProvCertif: tfd.getAttribute("RfcProvCertif") || "",
          selloCFD: tfd.getAttribute("SelloCFD") || "",
          selloSAT: tfd.getAttribute("SelloSAT") || "",
          noCertificadoSAT: tfd.getAttribute("NoCertificadoSAT") || "",
          noCertificado: comprobante ? comprobante.getAttribute("NoCertificado") : "",
          rfcEmisor: emisor ? emisor.getAttribute("Rfc") : "",
          regimenEmisor: emisor ? emisor.getAttribute("RegimenFiscal") : "",
          rfcReceptor: receptor ? receptor.getAttribute("Rfc") : "",
          regimenReceptor: receptor ? receptor.getAttribute("RegimenFiscalReceptor") : "",
          domicilioReceptor: receptor ? receptor.getAttribute("DomicilioFiscalReceptor") : "",
          total: comprobante ? comprobante.getAttribute("Total") : "0.00",
          fechaEmision: fechaEmi.replace("T", " ")
        };
      } catch (e) {
        return null;
      }
    },

    async downloadPDF() { 
      if (!this.detallePedido) return;

      try {
        const doc = new jsPDF('p', 'mm', 'letter');
        const pedido = this.detallePedido;
        const esFactura = !!pedido.factura;
        const fiscalData = this.getFiscalData(pedido.xml) || {};
        
        doc.setFont("helvetica", "bold");
        doc.setFontSize(11);
        doc.text("PARAL DISTRIBUIDORA DE REFRESCOS", 14, 15);
        
        try {
          doc.addImage(this.logoBase64, 'PNG', 14, 25, 45, 14);
        } catch (e) {
          console.warn("No se pudo cargar el logo", e);
        }

        doc.setFillColor(34, 139, 34); 
        doc.rect(110, 10, 95, 4, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(8);
        doc.text(esFactura ? "Comprobante Fiscal Digital" : "Nota de Venta", 157.5, 13, { align: "center" });

        const greenLabels = [ "I - Ingreso:", "Fecha Emisión:", "Fecha Timbrado:", "Condiciones:", "UUID:", "No. Certificado:", "Certificado SAT:", "FrogId:" ];
        const greenVals = [
          pedido.factura || "N/A", 
          fiscalData.fechaEmision || this.formatDate(pedido.fecha_pedido),
          fiscalData.fechaTimbrado || "N/A",
          "CONTADO",
          fiscalData.uuid || "N/A",
          fiscalData.noCertificado || "N/A",
          fiscalData.noCertificadoSAT || "N/A",
          pedido.referencia_frog || pedido.pedido_id
        ];

        let startY = 18;
        for(let i=0; i<greenLabels.length; i++) {
          doc.setTextColor(34, 139, 34); doc.setFont("helvetica", "bold"); doc.text(greenLabels[i], 110, startY);
          doc.setTextColor(0, 0, 0); doc.setFont("helvetica", "normal"); doc.text(greenVals[i], 145, startY); startY += 3.5;
        }

        doc.setFontSize(8); doc.setTextColor(0,0,0); doc.setFont("helvetica", "bold");
        doc.text(`RFC Proveedor que Certifica: `, 14, 48); doc.setFont("helvetica", "normal"); doc.text(fiscalData.rfcProvCertif || "N/A", 54, 48);

        doc.setFillColor(150, 150, 150); doc.rect(14, 50, 191, 4, 'F'); doc.setTextColor(255, 255, 255); doc.setFont("helvetica", "bold");
        doc.text("Datos del emisor", 60, 53, { align: "center" }); doc.text("Datos del receptor", 155, 53, { align: "center" });

        doc.setTextColor(0, 0, 0); doc.setFontSize(8);
        const rowH = 3.5; let emisorY = 58;
        
        doc.setFont("helvetica", "bold"); doc.text("Lugar de expedición:", 14, emisorY); doc.setFont("helvetica", "normal"); doc.text("29020", 45, emisorY); emisorY += rowH;
        doc.setFont("helvetica", "bold"); doc.text("R.F.C:", 14, emisorY); doc.setFont("helvetica", "normal"); doc.text(fiscalData.rfcEmisor || "PDR011126692", 45, emisorY); emisorY += rowH;
        doc.setFont("helvetica", "bold"); doc.text("Teléfono:", 14, emisorY); doc.setFont("helvetica", "normal"); doc.text("9611416440", 45, emisorY); emisorY += rowH;
        doc.setFont("helvetica", "bold"); doc.text("Calle:", 14, emisorY); doc.setFont("helvetica", "normal"); doc.text("CARRETERA TUXTLA - CHICOASEN KM4", 45, emisorY); emisorY += rowH;
        doc.setFont("helvetica", "bold"); doc.text("No. Ext:", 14, emisorY); doc.setFont("helvetica", "normal"); doc.text("124", 45, emisorY); doc.setFont("helvetica", "bold"); doc.text("No. Int:", 80, emisorY); emisorY += rowH;
        doc.setFont("helvetica", "bold"); doc.text("Colonia:", 14, emisorY); doc.setFont("helvetica", "normal"); doc.text("PLAN DE AYALA", 45, emisorY); emisorY += rowH;
        doc.setFont("helvetica", "bold"); doc.text("Municipio:", 14, emisorY); doc.setFont("helvetica", "normal"); doc.text("TUXTLA GUTIERREZ", 45, emisorY); emisorY += rowH;
        doc.setFont("helvetica", "bold"); doc.text("Estado:", 14, emisorY); doc.setFont("helvetica", "normal"); doc.text("CHIAPAS", 45, emisorY); emisorY += rowH;
        doc.setFont("helvetica", "bold"); doc.text("CP:", 14, emisorY); doc.setFont("helvetica", "normal"); doc.text("29020", 45, emisorY);
        
        const cli = this.user || {};
        const direcciones = cli.direcciones || [];
        const dirFiscal = (direcciones.length > 0) ? (direcciones.find(d => d.CTEDIR_TIPOFIS == 1) || direcciones[0]) : {};
        const dirEntrega = (direcciones.length > 0) ? (direcciones.find(d => d.CTEDIR_TIPOENT == 1) || direcciones[0]) : {};

        let recY = 58;
        doc.setFont("helvetica", "bold"); doc.text("Datos del receptor:", 110, recY); doc.setFont("helvetica", "normal"); doc.text(`${this.user.cliente || ''}   ${this.user.razon || 'MOSTRADOR'}`, 135, recY); recY += rowH;
        doc.setFont("helvetica", "bold"); doc.text("R.F.C:", 110, recY); doc.setFont("helvetica", "normal"); doc.text(fiscalData.rfcReceptor || "XAXX010101000", 135, recY); recY += rowH;
        doc.setFont("helvetica", "bold"); doc.text("Email:", 110, recY); doc.setFont("helvetica", "normal"); doc.text(cli.email || "N/A", 135, recY); recY += rowH;
        doc.setFont("helvetica", "bold"); doc.text("Uso CFDI:", 110, recY); doc.setFont("helvetica", "normal"); doc.text(pedido.uso_cfdi || "G03", 135, recY); recY += rowH;
        
        const regimenes = {
            "601": "General de Ley Personas Morales",
            "603": "Personas Morales con Fines no Lucrativos",
            "605": "Sueldos y Salarios e Ingresos Asimilados a Salarios",
            "606": "Arrendamiento",
            "607": "Régimen de Enajenación o Adquisición de Bienes",
            "608": "Demás ingresos",
            "609": "Consolidación",
            "610": "Residentes en el Extranjero sin Establecimiento Permanente en México",
            "611": "Ingresos por Dividendos (socios y accionistas)",
            "612": "Personas Físicas con Actividades Empresariales y Profesionales",
            "614": "Intereses",
            "615": "Régimen de los ingresos por obtención de premios",
            "616": "Sin obligaciones fiscales",
            "620": "Sociedades Cooperativas de Producción que optan por diferir sus ingresos",
            "621": "Incorporación Fiscal",
            "622": "Actividades Agrícolas, Ganaderas, Silvícolas y Pesqueras",
            "623": "Opcional para Grupos de Sociedades",
            "624": "Coordinados",
            "625": "Régimen de las Actividades Empresariales con ingresos a través de Plataformas Tecnológicas",
            "626": "Régimen Simplificado de Confianza"
        };
        const regCode = fiscalData.regimenReceptor || "612";
        const regText = regimenes[regCode] ? `${regCode} - ${regimenes[regCode]}` : regCode;
        doc.setFont("helvetica", "bold"); doc.text("Régimen Fiscal:", 110, recY); doc.setFont("helvetica", "normal"); 
        const splitRegimen = doc.splitTextToSize(regText, 65);
        doc.text(splitRegimen, 135, recY); recY += (splitRegimen.length * rowH);

        doc.setFont("helvetica", "bold"); doc.text("Calle:", 110, recY); doc.setFont("helvetica", "normal"); doc.text(dirFiscal.CTEDIR_CALLE || "CONOCIDO", 135, recY); recY += rowH;
        doc.setFont("helvetica", "bold"); doc.text("No. Ext:", 110, recY); doc.setFont("helvetica", "normal"); doc.text(dirFiscal.CTEDIR_CALLENUMEXT || "SN", 135, recY); doc.setFont("helvetica", "bold"); doc.text("No. Int:", 170, recY); recY += rowH;
        doc.setFont("helvetica", "bold"); doc.text("Municipio:", 110, recY); doc.setFont("helvetica", "normal"); doc.text(dirFiscal.CTEDIR_MUNICIPIO || "CHIAPAS", 135, recY); recY += rowH;
        doc.setFont("helvetica", "bold"); doc.text("Estado:", 110, recY); doc.setFont("helvetica", "normal"); doc.text("CHIAPAS", 135, recY); doc.setFont("helvetica", "bold"); doc.text("Domicilio Fiscal:", 170, recY); doc.setFont("helvetica", "normal"); doc.text(fiscalData.domicilioReceptor || "29000", 195, recY); recY += rowH;

        let midY = Math.max(92, recY + 2);
        doc.setDrawColor(0); doc.setLineWidth(0.5); doc.line(14, midY, 205, midY); 
        
        doc.setFont("helvetica", "bold"); doc.text("Entregar en:", 14, midY + 4);
        
        doc.setFont("helvetica", "bold"); doc.text("Den. Comercial:", 110, midY + 4); 
        doc.setFont("helvetica", "normal"); 
        doc.text(cli.denominacion_comercial || "N/A", 135, midY + 4);

        doc.setFont("helvetica", "bold"); doc.text("Calle:", 14, midY + 8); 
        doc.setFont("helvetica", "normal"); 
        doc.text(dirEntrega.CTEDIR_CALLE || "N/A", 30, midY + 8);

        doc.setFont("helvetica", "bold"); doc.text("Núm:", 85, midY + 8); 
        doc.setFont("helvetica", "normal"); 
        doc.text(dirEntrega.CTEDIR_CALLENUMEXT || "SN", 95, midY + 8);

        doc.setFont("helvetica", "bold"); doc.text("Colonia:", 14, midY + 12); 
        doc.setFont("helvetica", "normal"); 
        doc.text(dirEntrega.CTEDIR_COLONIA || "N/A", 30, midY + 12);

        doc.setFont("helvetica", "bold"); doc.text("Municipio:", 110, midY + 12); 
        doc.setFont("helvetica", "normal"); 
        doc.text(dirEntrega.CTEDIR_MUNICIPIO || "TUXTLA GUTIERREZ", 135, midY + 12);

        doc.setFont("helvetica", "bold"); doc.text("Estado:", 170, midY + 12); 
        doc.setFont("helvetica", "normal"); 
        const estadoTexto = (dirEntrega.C_ESTADO_K === 'CHP') ? 'CHIAPAS' : (dirEntrega.CTEDIR_ESTADO || 'CHIAPAS');
        doc.text(estadoTexto, 185, midY + 12);


        if (esFactura) {
          doc.line(14, midY + 14, 205, midY + 14);
          
          doc.setFont("helvetica", "bold"); doc.text("CFDI Relacionados:", 14, midY + 18);
          doc.text("Tipo de relación:", 14, midY + 22); 
          doc.setFont("helvetica", "normal");

          doc.setFont("helvetica", "bold"); doc.text("Ref. Factura:", 80, midY + 22); 
          doc.setFont("helvetica", "normal");

          doc.setFont("helvetica", "bold"); doc.text("UUID's:", 130, midY + 22); 
          doc.setFont("helvetica", "normal");

          doc.rect(14, midY + 24, 191, 4);
          doc.setFont("helvetica", "bold"); doc.text("Información Global:", 14, midY + 34);
          
          doc.text("Periodicidad:", 14, midY + 38); 
          doc.setFont("helvetica", "normal");

          doc.setFont("helvetica", "bold"); doc.text("Mes:", 14, midY + 42); 
          doc.setFont("helvetica", "normal");

          doc.setFont("helvetica", "bold"); doc.text("Año:", 14, midY + 46); 
          doc.setFont("helvetica", "normal");
        }

        const tableData = [];
        pedido.detalles.forEach(item => {
          tableData.push([ { content: item.codigo, styles: { fontStyle: 'normal' } }, { content: item.cantidad.toFixed(2), styles: { fontStyle: 'normal' } }, { content: item.unidad, styles: { fontStyle: 'normal' } }, { content: item.nombre, styles: { fontStyle: 'normal' } }, { content: this.formatMoney(item.PEDCTED_COSTO), styles: { fontStyle: 'normal' } }, { content: this.formatMoney(item.descuento), styles: { fontStyle: 'normal' } }, { content: this.formatMoney(item.subtotal), styles: { fontStyle: 'normal', halign: 'right' } } ]);
          tableData.push([ { content: item.claveSat, styles: { fontStyle: 'normal' } }, { content: '' }, { content: item.claveUnidadSat, styles: { fontStyle: 'normal' } }, { content: 'Agua mineral / Refresco', styles: { fontStyle: 'normal' } }, { content: '' }, { content: 'Objeto Impuesto:', styles: { fontStyle: 'bold' } }, { content: item.objetoImp, styles: { fontStyle: 'normal', halign: 'right' } } ]);
          tableData.push([ { content: 'Impuestos Trasladados', colSpan: 7, styles: { fontStyle: 'bold', cellPadding: {top: 1, bottom: 0, left: 0} } } ]);
          let importeIVA = item.impuesto > 0 ? item.impuesto : (item.subtotal * 0.16);
          let baseImp = importeIVA / 0.16;
          tableData.push([ { content: `Base\n${this.formatMoney(baseImp)}`, colSpan: 2 }, { content: `Impuesto\n002`, colSpan: 1 }, { content: `TipoFactor\nTasa`, colSpan: 1 }, { content: `TasaOCuota\n0.160000`, colSpan: 1 }, { content: `Importe\n${this.formatMoney(importeIVA)}`, colSpan: 2, styles: { halign: 'left' } } ]);
          tableData.push([ { content: 'Impuestos Retenidos\n ', colSpan: 7, styles: { fontStyle: 'bold', cellPadding: {top: 1, bottom: 2, left: 0} } } ]);
        });

        doc.autoTable({
          startY: midY + 50,
          head: [["Código", "Cantidad", "U. M.", "Descripción", "P. Unitario", "Descuento", {content: "Importe", styles: {halign: 'right'}}]],
          body: tableData, theme: 'plain', styles: { fontSize: 7, cellPadding: 0.5, textColor: [0,0,0] }, headStyles: { fontStyle: 'bold', lineWidth: { top: 0.5, bottom: 0.5 }, lineColor: 0 }, margin: { left: 14, right: 10 }
        });

        let finalY = doc.lastAutoTable.finalY + 2;
        if (finalY + 40 > doc.internal.pageSize.height) { doc.addPage(); finalY = 20; }

        const totalCantidad = pedido.detalles.reduce((acc, item) => acc + Number(item.cantidad), 0);
        doc.setLineWidth(0.5); doc.line(14, finalY, 205, finalY);
        doc.setFont("helvetica", "bold"); doc.text("Total:", 14, finalY + 4); doc.setFont("helvetica", "normal"); doc.text(totalCantidad.toString(), 30, finalY + 4);

        doc.setFont("helvetica", "bold"); doc.text("Cantidad en Letra:", 14, finalY + 8); doc.setFont("helvetica", "normal"); doc.text(`(${this.numeroALetras(pedido.total)})`, 50, finalY + 8);
        doc.setFont("helvetica", "bold"); doc.text("Método de Pago:", 14, finalY + 12); doc.setFont("helvetica", "normal"); doc.text(pedido.metodo_pago || "PUE", 45, finalY + 12); doc.text("Pago en una sola exhibición", 60, finalY + 12);
        doc.setFont("helvetica", "bold"); doc.text("Forma de Pago:", 14, finalY + 16); doc.setFont("helvetica", "normal"); doc.text(pedido.forma_pago || "01", 45, finalY + 16); doc.text("Efectivo", 60, finalY + 16);
        doc.setFont("helvetica", "bold"); doc.text("Núm de Cuenta:", 14, finalY + 20);
        doc.setFont("helvetica", "bold"); doc.text("Moneda:", 110, finalY + 16); doc.setFont("helvetica", "normal"); doc.text("MXN Pesos Mexicanos", 135, finalY + 16);
        doc.setFont("helvetica", "bold"); doc.text("Tipo de Cambio:", 110, finalY + 20); doc.setFont("helvetica", "normal"); doc.text("1.00", 135, finalY + 20);

        let subtotalNeto = pedido.total - (pedido.impuestos || 0);
        doc.setFont("helvetica", "bold"); doc.text("Subtotal:", 165, finalY + 8); doc.setFont("helvetica", "normal"); doc.text(this.formatMoney(subtotalNeto), 205, finalY + 8, {align: 'right'});
        doc.setFont("helvetica", "bold"); doc.text("Descuento:", 165, finalY + 12); doc.setFont("helvetica", "normal"); doc.text(this.formatMoney(pedido.descuentos), 205, finalY + 12, {align: 'right'});
        doc.setFont("helvetica", "bold"); doc.text("IEPS:", 165, finalY + 16); doc.setFont("helvetica", "normal"); doc.text("0.00", 205, finalY + 16, {align: 'right'});
        doc.setFont("helvetica", "bold"); doc.text("IVA:", 165, finalY + 20); doc.setFont("helvetica", "normal"); doc.text(this.formatMoney(pedido.impuestos), 205, finalY + 20, {align: 'right'});
        doc.setFont("helvetica", "bold"); doc.text("IEPS Ret.:", 165, finalY + 24); doc.setFont("helvetica", "normal"); doc.text("0.00", 205, finalY + 24, {align: 'right'});
        doc.setFont("helvetica", "bold"); doc.text("Total:", 165, finalY + 28); doc.setFont("helvetica", "normal"); doc.text(this.formatMoney(pedido.total), 205, finalY + 28, {align: 'right'});

        if (esFactura && fiscalData.uuid) {
          let qrY = finalY + 35;
          if (qrY + 40 > doc.internal.pageSize.height) { doc.addPage(); qrY = 20; }
          try {
            const qrUrl = `https://verificacfdi.facturaelectronica.sat.gob.mx/default.aspx?id=${fiscalData.uuid}&re=${fiscalData.rfcEmisor}&rr=${fiscalData.rfcReceptor}&tt=${fiscalData.total}&fe=${fiscalData.selloCFD.slice(-8)}`;
            const qrImage = await QRCode.toDataURL(qrUrl, { margin: 0, width: 120 });
            doc.addImage(qrImage, 'PNG', 14, qrY, 30, 30);
          } catch (err) { console.log("Error generando QR:", err)}

          doc.setFontSize(6); doc.setFont("helvetica", "bold"); doc.text("Cadena original del complemento de certification digital del SAT:", 48, qrY + 2);
          doc.setFont("helvetica", "normal"); const cadena = `||1.1|${fiscalData.uuid}|${fiscalData.fechaTimbrado}|${fiscalData.rfcProvCertif}|${fiscalData.selloCFD}|${fiscalData.noCertificadoSAT}||`;
          doc.text(cadena, 48, qrY + 5, { maxWidth: 155 });
          doc.setFont("helvetica", "bold"); doc.text("Sello Digital del CFDI:", 48, qrY + 15); doc.setFont("helvetica", "normal"); doc.text(fiscalData.selloCFD, 48, qrY + 18, { maxWidth: 155 });
          doc.setFont("helvetica", "bold"); doc.text("Sello Digital del SAT:", 48, qrY + 27); doc.setFont("helvetica", "normal"); doc.text(fiscalData.selloSAT, 48, qrY + 30, { maxWidth: 155 });
        }

        const nombreBruto = esFactura ? `Factura_${pedido.factura}` : `Pedido_${pedido.pedido_id}`;
        // eslint-disable-next-line no-useless-escape
        const nombreArchivo = nombreBruto.replace(/[\/\s\\:*?"<>|]/g, '_') + '.pdf'; 
        const nombreXml = nombreArchivo.replace('.pdf', '.xml');

        let xmlResult = null;
        if (pedido.xml) {
            const decodedXml = this.decodeXML(pedido.xml); 
            if (Capacitor.isNativePlatform()) {
                xmlResult = await Filesystem.writeFile({ path: nombreXml, data: decodedXml, directory: Directory.Cache, encoding: Encoding.UTF8 });
            } else {
                const blob = new Blob([decodedXml], { type: "text/xml;charset=utf-8" });
                const link = document.createElement("a"); link.href = URL.createObjectURL(blob); link.download = nombreXml;
                document.body.appendChild(link); link.click(); document.body.removeChild(link);
            }
        }

        if (Capacitor.isNativePlatform()) {
          const pdfBase64 = doc.output('datauristring').split(',')[1];
          const result = await Filesystem.writeFile({
            path: nombreArchivo,
            data: pdfBase64,
            directory: Directory.Cache
          });
          
          let filesToShare = [result.uri];
          if (xmlResult) filesToShare.push(xmlResult.uri);

          await Share.share({ title: "Compartir Documentos", files: filesToShare, dialogTitle: 'Compartir Documentos Oficiales' });
        } else {
          doc.save(nombreArchivo);
        }

      } catch (error) {
        console.error("Error generando PDF:", error);
        alert("Ocurrió un error al generar el documento.");
      }
    },
    
    getNamesMapFromXML(xmlString) {
      if (!xmlString) return {};
      try {
        const decoded = this.decodeXML(xmlString); const parser = new DOMParser(); const xmlDoc = parser.parseFromString(decoded, "text/xml");
        const conceptos = xmlDoc.getElementsByTagName("cfdi:Concepto"); const map = {};
        for (let i = 0; i < conceptos.length; i++) {
          const id = conceptos[i].getAttribute("NoIdentificacion"); const desc = conceptos[i].getAttribute("Descripcion"); const claveSat = conceptos[i].getAttribute("ClaveProdServ"); const claveUnidadSat = conceptos[i].getAttribute("ClaveUnidad"); const objetoImp = conceptos[i].getAttribute("ObjetoImp");
          if (id) { map[id] = { desc, claveSat, claveUnidadSat, objetoImp }; }
        }
        return map;
      } catch (e) { return {}; }
    },

    getDetailsFromXML(xmlString) {
      if (!xmlString) return [];
      try {
        const decoded = this.decodeXML(xmlString); const parser = new DOMParser(); const xmlDoc = parser.parseFromString(decoded, "text/xml");
        const conceptos = xmlDoc.getElementsByTagName("cfdi:Concepto"); const detalles = [];
        for (let i = 0; i < conceptos.length; i++) {
          const c = conceptos[i];
          const cantidad = parseFloat(c.getAttribute("Cantidad") || "0");
          const valorUnitario = parseFloat(c.getAttribute("ValorUnitario") || "0");
          const importe = parseFloat(c.getAttribute("Importe") || "0");
          const descuento = parseFloat(c.getAttribute("Descuento") || "0");
          let impuestos = 0;
          const traslados = c.getElementsByTagName("cfdi:Traslado");
          for (let j = 0; j < traslados.length; j++) { impuestos += parseFloat(traslados[j].getAttribute("Importe") || "0"); }
          detalles.push({
            nombre: c.getAttribute("Descripcion"),
            claveSat: c.getAttribute("ClaveProdServ"),
            claveUnidadSat: c.getAttribute("ClaveUnidad"),
            objetoImp: c.getAttribute("ObjetoImp") || "02",
            codigo: c.getAttribute("NoIdentificacion") || "GENERICO",
            unidad: c.getAttribute("Unidad") || c.getAttribute("ClaveUnidad") || "PZA",
            cantidad: cantidad, PEDCTED_COSTO: valorUnitario, subtotal: importe + impuestos - descuento, descuento: descuento, impuesto: impuestos
          });
        }
        return detalles;
      } catch (e) { return []; }
    },

    decodeXML(xmlString) {
      if (!xmlString) return "";
      const txt = document.createElement("textarea"); txt.innerHTML = xmlString; return txt.value;
    },

    numeroALetras(amount) {
      if (!amount) return "CERO PESOS 00/100 M.N.";
      const numero = parseFloat(amount);
      const enteros = Math.floor(numero);
      const centavos = Math.round((numero - enteros) * 100);
      const centavosStr = centavos < 10 ? "0" + centavos : centavos;
      const leerTres = (n) => {
        const u = ["", "UN", "DOS", "TRES", "CUATRO", "CINCO", "SEIS", "SIETE", "OCHO", "NUEVE"];
        const d = ["", "DIEZ", "VEINTE", "TREINTA", "CUARENTA", "CINCUENTA", "SESENTA", "SETENTA", "OCHENTA", "NOVENTA"];
        const d10 = ["DIEZ", "ONCE", "DOCE", "TRECE", "CATORCE", "QUINCE", "DIECISEIS", "DIECISIETE", "DIECIOCHO", "DIECINUEVE"];
        const c = ["", "CIENTO", "DOSCIENTOS", "TRESCIENTOS", "CUATROCIENTOS", "QUINIENTOS", "SEISCIENTOS", "SETECIENTOS", "OCHOCIENTOS", "NOVECIENTOS"];
        let str = "";
        if (n === 100) return "CIEN";
        if (n >= 100) { str += c[Math.floor(n / 100)] + " "; n %= 100; }
        if (n >= 10 && n <= 19) { str += d10[n - 10] + " "; }
        else if (n >= 20) { str += d[Math.floor(n / 10)]; if (n % 10 > 0) str += " Y " + u[n % 10]; str += " "; }
        else if (n > 0) { str += u[n] + " "; }
        return str.trim();
      };
      let letras = "";
      if (enteros === 0) letras = "CERO";
      else if (enteros >= 1000000) {
        const millon = Math.floor(enteros / 1000000); const resto = enteros % 1000000;
        if (millon === 1) letras += "UN MILLON "; else letras += leerTres(millon) + " MILLONES ";
        if (resto > 0) { if (resto >= 1000) { const miles = Math.floor(resto / 1000); const r = resto % 1000; if (miles === 1) letras += "MIL "; else letras += leerTres(miles) + " MIL "; if (r > 0) letras += leerTres(r); } else { letras += leerTres(resto); } }
      } else if (enteros >= 1000) {
        const miles = Math.floor(enteros / 1000); const resto = enteros % 1000;
        if (miles === 1) letras += "MIL "; else letras += leerTres(miles) + " MIL "; if (resto > 0) letras += leerTres(resto);
      } else { letras += leerTres(enteros); }
      return `${letras.trim()} PESOS ${centavosStr}/100 M.N.`;
    },

    async downloadXML() {
      if (!this.detallePedido?.xml) return;
      const decodedXml = this.decodeXML(this.detallePedido.xml); 
      const nombreBruto = this.detallePedido.factura ? `Factura_${this.detallePedido.factura}` : `Pedido_${this.detallePedido.pedido_id}`;
      // eslint-disable-next-line no-useless-escape
      const fileName = nombreBruto.replace(/[\/\s\\:*?"<>|]/g, '_') + '.xml';
      if (Capacitor.isNativePlatform()) {
        try {
          const result = await Filesystem.writeFile({ path: fileName, data: decodedXml, directory: Directory.Cache, encoding: Encoding.UTF8 });
          await Share.share({ title: fileName, url: result.uri, dialogTitle: 'Compartir Archivo XML' });
        } catch (err) { console.error("Error guardando XML nativo", err); alert("Error guardando el XML en el dispositivo."); }
      } else {
        const blob = new Blob([decodedXml], { type: "text/xml;charset=utf-8" });
        const link = document.createElement("a"); link.href = URL.createObjectURL(blob); link.download = fileName;
        document.body.appendChild(link); link.click(); document.body.removeChild(link);
      }
    },

    verXML() {
      if (!this.detallePedido?.xml) return;
      const blob = new Blob([this.decodeXML(this.detallePedido.xml)], { type: "text/xml;charset=utf-8" });
      window.open(URL.createObjectURL(blob), "_blank");
    }
  },
  mounted() { 
    const checkLogin = () => {
      if (!this.user.cliente && !this.isComponent) {
        this.localLoading = false;
        this.$router.replace("/login");
      } else if (!this.user.cliente && this.isComponent) {
        this.localLoading = false;
      } else if (this.user.cliente && this.localPedidos.length === 0 && !this.localLoadingFetchTriggered) {
        this.localLoadingFetchTriggered = true;
        this.obtenerConsultaPedidos();
      }
    };

    if (!this.userStore.isSessionReady) {
      this.userStore.initSession().then(() => checkLogin());
    } else {
      checkLogin();
    }
  }
};
</script>

<style scoped>
.status-strip { height: 5px; width: 100%; }
.bg-blue { background-color: #2196F3 !important; }
.bg-orange { background-color: #FF9800 !important; }
.bg-purple { background-color: #9C27B0 !important; }
.bg-green { background-color: #4CAF50 !important; }
.bg-red { background-color: #F44336 !important; }
.bg-grey { background-color: #9E9E9E !important; }
.factura-card { transition: transform 0.15s, box-shadow 0.15s; }
.factura-card:active { transform: scale(0.98); }
.border-dashed { border-style: dashed !important; }
.line-height-1 { line-height: 1.1; }
.gap-2 { gap: 8px; }
.scroll-container {
  height: 100vh;         /* Toma el alto total de la pantalla */
  overflow-y: auto;      /* Habilita el scroll vertical mágicamente */
  padding-top: 70px;     /* Evita que el appbar tape el primer pedido (ajusta los px si tu header es más alto) */
  padding-bottom: 90px;  /* Evita que el bottom-navigation tape el último pedido */
  -webkit-overflow-scrolling: touch; /* Hace que el scroll sea fluido en teléfonos (iOS/Android) */
}
</style>